/*
 * <product-chat-fab> — vanilla-JS custom element for the Yalo product chat.
 *
 * Replaces the original `yalo-product-chat-fab.js`, which imported
 * `@theme/component` and used `ref=` / `on:` bindings from Shopify's Horizon
 * theme framework — neither exists in this Dawn-based theme.
 *
 * The chat lives in a <dialog>:
 *   Desktop — opened non-modally (`dialog.show()`), so it renders inline in the
 *     product info column; the pill is hidden via CSS.
 *   Mobile  — the pill opens it with `dialog.showModal()`, so it renders in the
 *     browser top layer as a bottom sheet. The top layer is immune to a
 *     transformed ancestor's containing block, and gives us a native ::backdrop,
 *     focus trap and Esc-to-close for free.
 *
 * Public surface the chat config script relies on: `open()` / `close()`.
 */
class ProductChatFab extends HTMLElement {
  #controller = new AbortController();
  #mobile = null;

  connectedCallback() {
    // The pill is rendered OUTSIDE this element (outside the transformed product
    // info column, so its position:fixed anchors to the viewport). Find it by the
    // shared chat id rather than as a descendant.
    this.pill = this.querySelector('.yalo-chat-mobile-button');
    this.dialog = this.querySelector('dialog.yalo-chat-container');

    const { signal } = this.#controller;
    this.pill?.addEventListener('click', this.open, { signal });

    // A click on the ::backdrop lands on the dialog element itself — close it.
    this.dialog?.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) this.close();
      },
      { signal }
    );

    // Esc / backdrop / SDK-driven close all funnel through the native 'close'
    // event, so clean up here in one place.
    this.dialog?.addEventListener(
      'close',
      () => {
        this.pill?.setAttribute('aria-expanded', 'false');
        document.documentElement.removeAttribute('scroll-lock');
        if (this.#mobile.matches) this.pill?.focus();
      },
      { signal }
    );

    this.#mobile = matchMedia('(max-width: 749px)');
    this.#mobile.addEventListener('change', this.#syncMode, { signal });
    this.#syncMode();
  }

  /**
   * Reconciles the dialog with the current breakpoint: inline (non-modal) on
   * desktop, closed-until-pill on mobile.
   */
  #syncMode = () => {
    if (!this.dialog) return;

    if (this.#mobile.matches) {
      // Mobile: not inline. Drop the initial inline `open` so the pill can
      // showModal() (which throws if the dialog is already open).
      if (this.dialog.open && !this.dialog.matches(':modal')) this.dialog.close();
    } else {
      // Desktop: inline and always visible, never modal.
      if (this.dialog.matches(':modal')) this.dialog.close();
      if (!this.dialog.open) this.dialog.show();
      this.#showChatWindow();
    }
  };

  /** Opens the chat as a modal bottom sheet (mobile only). */
  open = () => {
    if (!this.dialog || !this.#mobile.matches || this.dialog.matches(':modal')) return;

    if (this.dialog.open) this.dialog.close(); // clear any inline-open state first
    this.dialog.showModal();
    this.pill?.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('scroll-lock', '');

    // Re-open the SDK chat window — its close button sets `open = false`.
    this.#showChatWindow();

    // The sheet only now has real dimensions; nudge the SDK to lay out.
    window.dispatchEvent(new Event('resize'));
  };

  /** Closes the modal sheet. No-op inline on desktop. */
  close = () => {
    if (this.dialog?.matches(':modal')) this.dialog.close();
  };

  #showChatWindow() {
    const chatWindow = this.querySelector('yalo-chat-window');
    if (chatWindow) chatWindow.open = true;
  }

  disconnectedCallback() {
    this.#controller.abort();
    document.documentElement.removeAttribute('scroll-lock');
  }
}

if (!customElements.get('product-chat-fab')) {
  customElements.define('product-chat-fab', ProductChatFab);
}
